/**
 * Campos do cadastro Canopus (KYC/PLD). Controlado: recebe o cliente e devolve
 * o cliente alterado. `mostrar(chave)` escolhe o que aparece — a venda usa para
 * pedir só as "informações complementares" que faltam; sem ele, mostra tudo.
 */
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { buscarProfissoesCanopus, type ClienteCanopus } from '@/lib/canopus-api';
import { DOCUMENTOS_PF, ESTADOS_CIVIS, precisaConjuge } from './venda-canopus';

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

/** Sugestões de profissão da Canopus (nome exato da lista dela). */
function useProfissoes(termo: string) {
  const [lista, setLista] = useState<string[]>([]);
  useEffect(() => {
    if (termo.trim().length < 3) { setLista([]); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;
      const r = await buscarProfissoesCanopus(termo, token).catch(() => []);
      if (vivo) setLista(r);
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [termo]);
  return lista;
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{rotulo}</Label>
      {children}
    </div>
  );
}

function SimNao({ rotulo, valor, aoMudar }: { rotulo: string; valor: boolean; aoMudar: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border px-3 py-2">
      <span className="text-sm">{rotulo}</span>
      <Select value={valor ? 'sim' : 'nao'} onValueChange={(v) => aoMudar(v === 'sim')}>
        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="nao">Não</SelectItem>
          <SelectItem value="sim">Sim</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoProfissao({ id, valor, aoMudar }: { id: string; valor: string; aoMudar: (v: string) => void }) {
  const sugestoes = useProfissoes(valor);
  return (
    <>
      <Input list={id} value={valor} placeholder="Profissão da lista (ex.: ENGENHEIRO (A))" onChange={(e) => aoMudar(e.target.value.toUpperCase())} />
      <datalist id={id}>{sugestoes.map((s) => <option key={s} value={s} />)}</datalist>
    </>
  );
}

type Props = { cliente: ClienteCanopus; aoMudar: (c: ClienteCanopus) => void; mostrar?: (chave: string) => boolean };

export function CanopusClienteCampos({ cliente, aoMudar, mostrar = () => true }: Props) {
  const set = <K extends keyof ClienteCanopus>(k: K, v: ClienteCanopus[K]) => aoMudar({ ...cliente, [k]: v });
  const cj = cliente.conjuge || { nome: '', cpf: '', rg: '', nascimento: '', rg_orgao: '', profissao: '', sexo: 'F' as const };
  const setConjuge = (k: keyof typeof cj, v: string) => aoMudar({ ...cliente, conjuge: { ...cj, [k]: v } });
  const texto = (k: keyof ClienteCanopus, placeholder = '') => (
    <Input value={String(cliente[k] ?? '')} placeholder={placeholder} onChange={(e) => set(k, e.target.value as never)} />
  );
  const algum = (...ks: string[]) => ks.some((k) => mostrar(k));
  // Cônjuge: quando o estado civil exige e ela estava incompleta, ou se o estado civil foi pedido agora.
  const verConjuge = precisaConjuge(cliente.estado_civil) && (mostrar('conjuge') || mostrar('estado_civil'));

  return (
    <div className="space-y-5">
      {algum('nome', 'cpf', 'nascimento', 'sexo', 'email', 'celular', 'telefone', 'renda') && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Cliente</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {mostrar('nome') && <Campo rotulo="Nome completo">{texto('nome')}</Campo>}
            {mostrar('cpf') && <Campo rotulo="CPF">{texto('cpf', 'só números')}</Campo>}
            {mostrar('nascimento') && <Campo rotulo="Nascimento">{texto('nascimento', 'DD/MM/AAAA')}</Campo>}
            {mostrar('sexo') && (
              <Campo rotulo="Sexo">
                <Select value={cliente.sexo} onValueChange={(v) => set('sexo', v as 'M' | 'F')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Masculino</SelectItem><SelectItem value="F">Feminino</SelectItem></SelectContent>
                </Select>
              </Campo>
            )}
            {mostrar('email') && <Campo rotulo="E-mail (recebe contrato e boleto)">{texto('email')}</Campo>}
            {mostrar('celular') && <Campo rotulo="Celular">{texto('celular', 'DDD + número')}</Campo>}
            {mostrar('telefone') && <Campo rotulo="Telefone fixo">{texto('telefone', 'pode repetir o celular')}</Campo>}
            {mostrar('renda') && <Campo rotulo="Renda mensal (R$)">{texto('renda', 'ex.: 15000')}</Campo>}
          </div>
        </section>
      )}

      {algum('rg', 'rg_emissao', 'rg_orgao', 'estado_civil', 'profissao', 'nome_mae', 'nome_pai', 'uf_nascimento', 'cidade_nascimento', 'nacionalidade') && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Documento e dados pessoais</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {mostrar('rg') && <Campo rotulo="RG">{texto('rg')}</Campo>}
            {mostrar('rg_emissao') && <Campo rotulo="Emissão do RG">{texto('rg_emissao', 'DD/MM/AAAA')}</Campo>}
            {mostrar('rg_orgao') && <Campo rotulo="Órgão emissor">{texto('rg_orgao', 'ex.: SSPPR')}</Campo>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {mostrar('estado_civil') && (
              <Campo rotulo="Estado civil">
                <Select value={cliente.estado_civil} onValueChange={(v) => set('estado_civil', v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{ESTADOS_CIVIS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </Campo>
            )}
            {mostrar('profissao') && (
              <Campo rotulo="Profissão (lista da Canopus)">
                <CampoProfissao id="canopus-profissao" valor={cliente.profissao} aoMudar={(v) => set('profissao', v)} />
              </Campo>
            )}
            {mostrar('nome_mae') && <Campo rotulo="Nome da mãe">{texto('nome_mae')}</Campo>}
            {mostrar('nome_pai') && <Campo rotulo="Nome do pai (opcional)">{texto('nome_pai')}</Campo>}
            {mostrar('nacionalidade') && <Campo rotulo="Nacionalidade">{texto('nacionalidade')}</Campo>}
            {mostrar('uf_nascimento') && (
              <Campo rotulo="UF de nascimento">
                <Select value={cliente.uf_nascimento} onValueChange={(v) => set('uf_nascimento', v)}>
                  <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Campo>
            )}
            {mostrar('cidade_nascimento') && <Campo rotulo="Cidade de nascimento">{texto('cidade_nascimento')}</Campo>}
          </div>
        </section>
      )}

      {verConjuge && (
        <section className="space-y-3 rounded-md border border-amber-300 p-3">
          <h3 className="text-sm font-semibold">Cônjuge (obrigatório para {cliente.estado_civil.toLowerCase()})</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rotulo="Nome"><Input value={cj.nome} onChange={(e) => setConjuge('nome', e.target.value)} /></Campo>
            <Campo rotulo="CPF"><Input value={cj.cpf} onChange={(e) => setConjuge('cpf', e.target.value)} /></Campo>
            <Campo rotulo="RG"><Input value={cj.rg} onChange={(e) => setConjuge('rg', e.target.value)} /></Campo>
            <Campo rotulo="Órgão emissor"><Input value={cj.rg_orgao} onChange={(e) => setConjuge('rg_orgao', e.target.value)} /></Campo>
            <Campo rotulo="Nascimento"><Input value={cj.nascimento} placeholder="DD/MM/AAAA" onChange={(e) => setConjuge('nascimento', e.target.value)} /></Campo>
            <Campo rotulo="Sexo">
              <Select value={cj.sexo} onValueChange={(v) => setConjuge('sexo', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="F">Feminino</SelectItem><SelectItem value="M">Masculino</SelectItem></SelectContent>
              </Select>
            </Campo>
            <Campo rotulo="Profissão (lista da Canopus)">
              <CampoProfissao id="canopus-profissao-conjuge" valor={cj.profissao} aoMudar={(v) => setConjuge('profissao', v)} />
            </Campo>
          </div>
        </section>
      )}

      {algum('cep', 'numero', 'complemento', 'endereco_comercial') && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Endereço</h3>
          <p className="text-xs text-muted-foreground">Rua, bairro e cidade a Canopus completa pelo CEP.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {mostrar('cep') && <Campo rotulo="CEP residencial">{texto('cep', 'só números')}</Campo>}
            {mostrar('numero') && <Campo rotulo="Número">{texto('numero')}</Campo>}
            {mostrar('complemento') && <Campo rotulo="Complemento">{texto('complemento')}</Campo>}
            {mostrar('endereco_comercial') && (
              <>
                <Campo rotulo="CEP comercial (vazio = o mesmo)">{texto('cep_comercial')}</Campo>
                <Campo rotulo="Número comercial">{texto('numero_comercial')}</Campo>
                <Campo rotulo="Complemento comercial">{texto('complemento_comercial')}</Campo>
              </>
            )}
          </div>
        </section>
      )}

      {algum('declaracoes', 'documentos_pf') && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Declarações (prevenção à lavagem de dinheiro)</h3>
          {mostrar('declaracoes') && (
            <>
              <SimNao rotulo="Pessoa politicamente exposta?" valor={cliente.pessoa_politicamente_exposta} aoMudar={(v) => set('pessoa_politicamente_exposta', v)} />
              <SimNao rotulo="Ocupou cargo público nos últimos 5 anos?" valor={cliente.ocupou_cargo_publico} aoMudar={(v) => set('ocupou_cargo_publico', v)} />
              <SimNao rotulo="Parente próximo ocupou cargo público (5 anos)?" valor={cliente.parente_cargo_publico} aoMudar={(v) => set('parente_cargo_publico', v)} />
              <Campo rotulo="Conta bancária">
                <Select value={cliente.conta_bancaria} onValueChange={(v) => set('conta_bancaria', v as ClienteCanopus['conta_bancaria'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao_desejo_informar">Não desejo informar</SelectItem>
                    <SelectItem value="nao_possuo">Não possuo conta bancária</SelectItem>
                  </SelectContent>
                </Select>
              </Campo>
            </>
          )}
          {mostrar('documentos_pf') && (
            <div className="space-y-1 pt-1">
              <Label className="text-xs">Documentos apresentados pelo cliente</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {DOCUMENTOS_PF.map((doc) => (
                  <label key={doc} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={cliente.documentos_pf.includes(doc)}
                      onCheckedChange={(m) => set('documentos_pf', m ? [...cliente.documentos_pf, doc] : cliente.documentos_pf.filter((x) => x !== doc))}
                    />
                    {doc.toLowerCase()}
                  </label>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
